/**
 * Pre-fill CRM manual intake form from lead, participants, or latest intake.
 */

import {
  emptyIntakePerson,
  emptyIntakeMinor,
} from '../components/hub/crm/CrmManualIntakeForm'

function splitDisplayName(displayName) {
  const cleaned = String(displayName || '')
    .replace(/\s*[—–-]\s*/g, ' ')
    .replace(/\s*&\s*/g, ' ')
    .trim()
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (!parts.length) return { first_name: '', last_name: '' }
  if (parts.length === 1) return { first_name: parts[0], last_name: '' }
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
}

function participantToPerson(participant) {
  if (!participant) return emptyIntakePerson()
  return {
    first_name: participant.first_name || '',
    last_name: participant.last_name || '',
    dob: participant.dob || '',
    email: participant.email || '',
    phone: participant.phone_raw || '',
  }
}

function mergePerson(base, patch) {
  const next = { ...base }
  for (const key of ['first_name', 'last_name', 'dob', 'email', 'phone']) {
    if (patch[key]) next[key] = patch[key]
  }
  return next
}

function findParticipant(participants, roles) {
  const roleSet = new Set(roles)
  return (
    participants.find((p) => roleSet.has(p.role) && p.is_primary_contact) ||
    participants.find((p) => roleSet.has(p.role))
  )
}

function contactFallback(contactSubmissions) {
  const row = contactSubmissions?.[0]
  if (!row) return null
  const { first_name, last_name } = splitDisplayName(row.name)
  return {
    first_name,
    last_name,
    dob: '',
    email: row.email || '',
    phone: row.phone || '',
  }
}

function resolveClientType(lead) {
  const t = lead?.client_type
  if (t === 'minor' || t === 'couple') return t
  return 'adult'
}

function fromLatestIntake(latestIntake) {
  const data = latestIntake.form_data || {}
  const clientType = latestIntake.client_type || 'adult'
  const serviceType = latestIntake.service_type || 'individual_counseling'
  const language = latestIntake.language === 'pt' ? 'pt' : 'en'

  if (clientType === 'minor' && data.minor) {
    return {
      clientType: 'minor',
      serviceType,
      language,
      adult: emptyIntakePerson(),
      person1: emptyIntakePerson(),
      person2: emptyIntakePerson(),
      minor: { ...emptyIntakeMinor(), ...data.minor },
    }
  }

  if (clientType === 'couple') {
    return {
      clientType: 'couple',
      serviceType,
      language,
      adult: emptyIntakePerson(),
      minor: emptyIntakeMinor(),
      person1: mergePerson(emptyIntakePerson(), data.person1 || {}),
      person2: mergePerson(emptyIntakePerson(), data.person2 || {}),
    }
  }

  return {
    clientType: 'adult',
    serviceType,
    language,
    minor: emptyIntakeMinor(),
    person1: emptyIntakePerson(),
    person2: emptyIntakePerson(),
    adult: mergePerson(emptyIntakePerson(), data.adult || {}),
  }
}

function fromLeadAndParticipants({ lead, participants, contactSubmissions }) {
  const clientType = resolveClientType(lead)
  const language = lead.preferred_language === 'pt' ? 'pt' : 'en'
  const serviceType = 'individual_counseling'
  const fallback = contactFallback(contactSubmissions)
  const leadContact = {
    ...splitDisplayName(lead.display_name),
    email: lead.email || '',
    phone: lead.phone_raw || '',
    dob: '',
  }

  if (clientType === 'minor') {
    const guardian =
      findParticipant(participants, ['guardian', 'contact']) ||
      (fallback ? { ...fallback, role: 'guardian' } : null)
    const minorPart = findParticipant(participants, ['minor'])
    const guardianName = guardian
      ? `${guardian.first_name || ''} ${guardian.last_name || ''}`.trim()
      : lead.display_name?.split('—')[0]?.trim() || ''

    return {
      clientType: 'minor',
      serviceType,
      language,
      adult: emptyIntakePerson(),
      person1: emptyIntakePerson(),
      person2: emptyIntakePerson(),
      minor: {
        ...emptyIntakeMinor(),
        guardian_name: guardianName || fallback?.first_name
          ? `${fallback.first_name} ${fallback.last_name}`.trim()
          : '',
        guardian_phone: guardian?.phone_raw || lead.phone_raw || fallback?.phone || '',
        guardian_email: guardian?.email || lead.email || fallback?.email || '',
        minor_full_name: minorPart
          ? `${minorPart.first_name || ''} ${minorPart.last_name || ''}`.trim()
          : lead.display_name?.split('—')[1]?.trim() || '',
        minor_dob: minorPart?.dob || '',
      },
    }
  }

  if (clientType === 'couple') {
    const p1 = findParticipant(participants, ['partner_1', 'adult', 'contact'])
    const p2 = findParticipant(participants, ['partner_2'])
    const person1 = mergePerson(
      emptyIntakePerson(),
      p1 ? participantToPerson(p1) : leadContact
    )
    const person2 = mergePerson(emptyIntakePerson(), p2 ? participantToPerson(p2) : {})

    return {
      clientType: 'couple',
      serviceType,
      language,
      adult: emptyIntakePerson(),
      minor: emptyIntakeMinor(),
      person1,
      person2,
    }
  }

  const primary =
    findParticipant(participants, ['adult', 'contact']) ||
    participants.find((p) => p.is_primary_contact) ||
    participants[0]

  const adult = mergePerson(
    emptyIntakePerson(),
    primary ? participantToPerson(primary) : mergePerson(emptyIntakePerson(), leadContact)
  )

  if (!adult.first_name && !adult.last_name && fallback) {
    adult.first_name = fallback.first_name
    adult.last_name = fallback.last_name
    adult.email = adult.email || fallback.email
    adult.phone = adult.phone || fallback.phone
  }

  return {
    clientType: 'adult',
    serviceType,
    language,
    minor: emptyIntakeMinor(),
    person1: emptyIntakePerson(),
    person2: emptyIntakePerson(),
    adult,
  }
}

/**
 * @param {{ lead: object, participants?: object[], contact_submissions?: object[], latest_intake?: object }} input
 */
export function buildManualIntakeInitialState({
  lead,
  participants = [],
  contact_submissions = [],
  latest_intake,
}) {
  if (latest_intake?.form_data) {
    return fromLatestIntake(latest_intake)
  }
  return fromLeadAndParticipants({
    lead,
    participants,
    contactSubmissions: contact_submissions,
  })
}
